// ============= Imports =============
import { useEffect, useRef, useCallback } from 'react';
import { useSignalR } from '@/contexts/signalR';

// ============= Types =============
export type SignalRHandler = (...args: unknown[]) => void;

export interface UseSignalRHubOptions {
  /**
   * The name of the hub to use (defaults to 'default')
   * @default 'default'
   */
  hubName?: string;
  /**
   * Whether to automatically start the connection when the hook mounts
   * @default true
   */
  autoStart?: boolean;
  /**
   * Whether to stop the connection when the hook unmounts
   * @default false (connection is managed globally)
   */
  autoStop?: boolean;
}

/**
 * Hook for subscribing to SignalR hub methods
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { on, off, invoke, isConnected } = useSignalRHub();
 *
 *   useEffect(() => {
 *     // Subscribe to a hub method
 *     const handler = (data: any) => {
 *       console.log('Received notification:', data);
 *     };
 *
 *     on('ReceiveNotification', handler);
 *
 *     // Cleanup
 *     return () => {
 *       off('ReceiveNotification', handler);
 *     };
 *   }, [on, off]);
 *
 *   const sendMessage = async () => {
 *     await invoke('SendMessage', { text: 'Hello' });
 *   };
 *
 *   return <div>Connected: {isConnected ? 'Yes' : 'No'}</div>;
 * };
 * ```
 */
export const useSignalRHub = (options: UseSignalRHubOptions = {}) => {
  const { hubName = 'default', autoStart = true, autoStop = false } = options;
  const {
    getConnection,
    isConnected: checkIsConnected,
    startConnection,
    stopConnection,
    invokeHub,
  } = useSignalR();
  const handlersRef = useRef<Map<string, Set<SignalRHandler>>>(new Map());

  // Get connection for this hub
  const connection = getConnection(hubName);
  const isConnected = checkIsConnected(hubName);

  // Start connection if needed (catch errors so hook never throws)
  useEffect(() => {
    if (autoStart && !isConnected) {
      startConnection(hubName).catch((err) => {
        const error = err instanceof Error ? err : new Error(String(err));
        console.error(`Failed to start SignalR hub [${hubName}]:`, error);
      });
    }
  }, [autoStart, isConnected, startConnection, hubName]);

  // Stop connection if needed (catch errors so hook never throws)
  useEffect(() => {
    if (autoStop) {
      return () => {
        stopConnection(hubName).catch((err) => {
          const error = err instanceof Error ? err : new Error(String(err));
          console.error(`Failed to stop SignalR hub [${hubName}]:`, error);
        });
      };
    }
  }, [autoStop, stopConnection, hubName]);

  // Subscribe to a hub method
  const on = useCallback(
    (methodName: string, handler: SignalRHandler) => {
      if (!connection) {
        console.warn(
          `⚠️ Cannot subscribe to ${methodName} on hub [${hubName}]: SignalR connection not available`
        );
        return;
      }

      // Store handler for cleanup
      if (!handlersRef.current.has(methodName)) {
        handlersRef.current.set(methodName, new Set());
      }
      handlersRef.current.get(methodName)?.add(handler);

      // Subscribe to SignalR event
      connection.on(methodName, handler);
      console.log(`📡 Subscribed to SignalR method [${hubName}].${methodName}`);
    },
    [connection, hubName]
  );

  // Unsubscribe from a hub method
  const off = useCallback(
    (methodName: string, handler: SignalRHandler) => {
      if (!connection) {
        return;
      }

      // Remove handler from storage
      handlersRef.current.get(methodName)?.delete(handler);

      // Unsubscribe from SignalR event
      connection.off(methodName, handler);
      console.log(
        `📴 Unsubscribed from SignalR method [${hubName}].${methodName}`
      );
    },
    [connection, hubName]
  );

  // Unsubscribe all handlers for a method
  const offAll = useCallback(
    (methodName: string) => {
      if (!connection) {
        return;
      }

      handlersRef.current.delete(methodName);
      connection.off(methodName);
      console.log(
        `📴 Unsubscribed all handlers from SignalR method [${hubName}].${methodName}`
      );
    },
    [connection, hubName]
  );

  // Invoke method on this hub
  const invokeOnHub = useCallback(
    async <T = unknown>(methodName: string, ...args: unknown[]): Promise<T> => {
      return invokeHub<T>(hubName, methodName, ...args);
    },
    [invokeHub, hubName]
  );

  // Cleanup all handlers on unmount
  useEffect(() => {
    return () => {
      if (connection) {
        handlersRef.current.forEach((handlers, methodName) => {
          handlers.forEach((handler) => {
            connection.off(methodName, handler);
          });
        });
        handlersRef.current.clear();
      }
    };
  }, [connection]);

  return {
    connection,
    isConnected,
    hubName,
    on,
    off,
    offAll,
    invoke: invokeOnHub,
    invokeHub: invokeOnHub,
    startConnection: () => startConnection(hubName),
    stopConnection: () => stopConnection(hubName),
  };
};
